import React from 'react'
import styled from 'styled-components'
import {
  FlipInputCurrencies,
  CurrencyInputItem,
  Page,
  PageContent,
  PageHeader,
  SettingsButton,
  Approvals,
  DisabledMessage,
  ApproveTokenForDelayButton,
} from '../../common'
import { ChainId, DISABLE_TRADING } from '../../constants'
import { useEthers } from '../../hooks'
import { Currency, CurrencyValue } from '../../model'
import { DISABLE_BUY } from '../../providers/settings/context'
import { getInputValue } from './getInputValue'
import { SwapButton } from './SwapButton'
import { SwapInfo } from './SwapInfo'
import { SwapState, useSwap } from './useSwap'
import { Comparator } from './Comparator'
import { logAmplitudeEvent } from '../../utils'

export function Swap() {
  const {
    currencies,
    firstRate,
    firstValue,
    setFirstValue,
    firstUsdValue,
    firstUsdValue18,
    secondRate,
    secondValue,
    setSecondValue,
    secondUsdValue,
    secondUsdValue18,
    swapFee,
    slippagePercent,
    setMax,
    state,
    controlled,
    showCalculatedValue,
    pair,
    exchangeQuotes,
    exchangeQuotesTimeoutStartAt,
    exchangeQuotesAutoRefresh,
    exchangeQuotesToggleAutoRefresh,
    exchangeQuotesRefresh,
  } = useSwap()

  function onSubmit() {
    setFirstValue('')
    setSecondValue('')
  }

  function onPairChange(currency: Currency, other: Currency) {
    setFirstValue('')
    setSecondValue('')
    currencies.setCurrencies(currency, other)
    logAmplitudeEvent('Swap: Change Pair', {
      pair: `${currency.symbol}-${other.symbol}`,
    })
  }

  const firstControlled = controlled === 'first'

  function onFlipClick() {
    if (firstControlled) {
      const otherValue = secondValue
      currencies.swap()
      setFirstValue(otherValue)
    } else {
      const otherValue = firstValue
      currencies.swap()
      setSecondValue(otherValue)
    }

    logAmplitudeEvent('Swap: Flip Tokens', {
      pair: `${currencies.second.symbol}-${currencies.first.symbol}`,
    })
  }

  const { chainId = ChainId.Mainnet } = useEthers()
  const disabled = pair && DISABLE_TRADING.includes(pair.address.toLowerCase())
  if (chainId === ChainId.Mainnet && disabled) {
    return (
      <Page>
        <PageContent>
          <PageHeaderStyled
            label="Swap"
            currency={currencies.first}
            other={currencies.second}
            onChange={onPairChange}
          />
          <DisabledMessage>
            <p>Swap on this pair is currently disabled.</p>
            <p>Please check back later or visit our Discord to learn more.</p>
          </DisabledMessage>
        </PageContent>
      </Page>
    )
  }

  return (
    <Page>
      <SideBySide>
        <Comparator
          controlled={controlled}
          currencyIn={currencies.first}
          currencyOut={currencies.second}
          firstValue={firstValue}
          secondValue={secondValue}
          quotes={exchangeQuotes}
          timeoutStartAt={exchangeQuotesTimeoutStartAt}
          autoRefresh={exchangeQuotesAutoRefresh}
          toggleAutoRefresh={exchangeQuotesToggleAutoRefresh}
          refresh={exchangeQuotesRefresh}
        />
        <PageContentStyled>
          <PageHeaderStyled
            label="Swap"
            currency={currencies.first}
            other={currencies.second}
            onChange={onPairChange}
          />
          <CurrencyInputItem
            label={`You Pay${firstControlled ? '' : ' (Estimated)'}`}
            rate={firstUsdValue || '-'}
            value={getInputValue(showCalculatedValue, controlled === 'first', firstValue)}
            onValueChange={(value) => {
              setFirstValue(value)
              logAmplitudeEvent('Swap: Input First', {
                pair: `${currencies.first.symbol}-${currencies.second.symbol}`,
                value,
                symbol: currencies.first.symbol,
              })
            }}
            onMax={setMax}
            currency={currencies.first}
          />
          <FlipInputCurrencies onClick={onFlipClick} />
          <CurrencyInputItem
            label={`You Receive${firstControlled ? ' (Estimated)' : ''}`}
            rate={secondUsdValue || '-'}
            value={getInputValue(showCalculatedValue, controlled === 'second', secondValue)}
            onValueChange={(value) => {
              setSecondValue(value)
              logAmplitudeEvent('Swap: Input Second', {
                pair: `${currencies.first.symbol}-${currencies.second.symbol}`,
                value,
                symbol: currencies.second.symbol,
              })
            }}
            onMax={setMax}
            currency={currencies.second}
            disabled={DISABLE_BUY}
          />
          {state === SwapState.NEED_FIRST_APPROVAL && (
            <Approvals>
              <ApproveTokenForDelayButton currency={currencies.first} />
            </Approvals>
          )}
          <SwapButton
            state={state}
            currencies={currencies}
            controlled={controlled}
            firstValue={firstValue}
            secondValue={secondValue}
            firstUsdValue18={firstUsdValue18}
            secondUsdValue18={secondUsdValue18}
            rate={secondRate}
            swapFee={swapFee}
            slippagePercent={slippagePercent}
            onSubmit={onSubmit}
          />
          <SwapInfo
            showCalculatedValue={showCalculatedValue}
            controlled={controlled}
            firstValue={CurrencyValue.fromString(currencies.first, firstValue)}
            secondValue={CurrencyValue.fromString(currencies.second, secondValue)}
            firstRate={firstRate}
            secondRate={secondRate}
            swapFee={swapFee}
            slippagePercent={slippagePercent}
          />
          <SettingsButton />
        </PageContentStyled>
      </SideBySide>
    </Page>
  )
}

const SideBySide = styled.div`
  margin: 30px auto 0 auto;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: flex-start;

  @media (max-width: 1024px) {
    flex-direction: column-reverse;
    justify-content: flex-start;
    align-items: center;
  }
`

const PageContentStyled = styled(PageContent)`
  margin: 0;
`

const PageHeaderStyled = styled(PageHeader)`
  margin-bottom: 20px;
`
